import useEventListener from "@/hooks/useEventListener";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";
import SearchIcon from "@mui/icons-material/Search";
import Button from "@mui/material/Button";
import FormControl, { type FormControlProps } from "@mui/material/FormControl";
import InputAdornment from "@mui/material/InputAdornment";
import OutlinedInput from "@mui/material/OutlinedInput";
import clsx from "clsx";
import type React from "react";
import { useRef, useState } from "react";

interface FloatingSearchProps
	extends Omit<FormControlProps, "onChange" | "onBlur"> {
	name?: string;
	value?: unknown;
	onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
	onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
	onClear?: () => void;
}

const FloatingSearch: React.FC<FloatingSearchProps> = (props) => {
	const {
		name,
		value,
		onChange,
		onBlur,
		onClear,
		className,
		children,
		...formControlProps
	} = props;
	const refSearch = useRef<HTMLDivElement>(null);
	const refInput = useRef<HTMLInputElement>(null);
	const [isExpanded, setIsExpanded] = useState(!!value);

	useOnClickOutside(refSearch, () => {
		if (value) return;
		setIsExpanded(false);
	});

	useEventListener(
		"click",
		() => {
			setIsExpanded(true);
		},
		refSearch,
	);

	return (
		<>
			{!isExpanded && children ? children : null}
			<FormControl
				ref={refSearch}
				focused={isExpanded}
				variant="outlined"
				size="small"
				fullWidth
				className={clsx(
					"!rounded-full !bg-white !overflow-hidden !transition-all !duration-500 !ease-in-out",
					{
						"!w-10 !cursor-pointer": !isExpanded,
						"!flex-1 !w-full !shadow-md": isExpanded,
					},
					className,
				)}
				{...formControlProps}
			>
				<OutlinedInput
					ref={refInput}
					name={name}
					placeholder="Search"
					aria-label="search"
					className="!rounded-full"
					startAdornment={
						<InputAdornment position="start">
							<SearchIcon
								className={clsx("text-zinc-950", {
									"-translate-x-1": !isExpanded,
								})}
							/>
						</InputAdornment>
					}
					value={value}
					onChange={onChange}
					onBlur={onBlur}
				/>
			</FormControl>
			{isExpanded ? (
				<Button variant="text" color="primary" onClick={onClear}>
					Cancel
				</Button>
			) : null}
		</>
	);
};

export default FloatingSearch;
